/**
 * FeatureUsageCard Component
 * Displays feature adoption and usage statistics
 *
 * Story 3.2: Feature Usage Statistics
 */

import React from 'react';
import { clsx } from 'clsx';
import { BarChart3, CheckSquare, MessageSquare, Link2, FileText, TrendingUp } from 'lucide-react';
import { Card, Badge } from '../../common';
import { ScopeBadge } from '../ReportScopeIndicator';
import type { ResolvedScope } from '../../../hooks/useReportData';

export interface FeatureUsageItem {
  feature: string;
  icon: 'check-ins' | 'comments' | 'alignments' | 'key-results' | 'objectives';
  usageCount: number;
  userCount: number;
  adoptionRate: number;
  trend: number;
}

export interface FeatureUsageData {
  features: FeatureUsageItem[];
  totalActiveFeatures: number;
  overallAdoptionRate: number;
}

export interface FeatureUsageCardProps {
  /** Feature usage data */
  data: FeatureUsageData;
  /** Loading state */
  isLoading?: boolean;
  /**
   * Resolved scope for THESE figures. For a scoped caller the API returns
   * `organization` here, not `team` — feature adoption is an aggregate over the
   * whole tenant and a team-sized denominator would make every rate wrong. The
   * badge exists so this card cannot inherit the page's team label.
   */
  scope?: ResolvedScope;
  /** Additional class names */
  className?: string;
}

/**
 * Get icon component for feature
 */
function getFeatureIcon(icon: FeatureUsageItem['icon']): React.ReactNode {
  switch (icon) {
    case 'check-ins':
      return <CheckSquare className="w-5 h-5" />;
    case 'comments':
      return <MessageSquare className="w-5 h-5" />;
    case 'alignments':
      return <Link2 className="w-5 h-5" />;
    case 'key-results':
      return <BarChart3 className="w-5 h-5" />;
    case 'objectives':
      return <FileText className="w-5 h-5" />;
    default:
      return <BarChart3 className="w-5 h-5" />;
  }
}

/**
 * Get adoption rate color
 */
function getAdoptionColor(rate: number): string {
  if (rate >= 80) return 'text-green-600';
  if (rate >= 50) return 'text-amber-600';
  return 'text-red-600';
}

/**
 * Get adoption bar color
 */
function getAdoptionBarColor(rate: number): string {
  if (rate >= 80) return 'bg-green-500';
  if (rate >= 50) return 'bg-amber-500';
  return 'bg-red-500';
}

/**
 * Loading skeleton
 */
const FeatureUsageSkeleton: React.FC = () => (
  <div className="animate-pulse space-y-4">
    <div className="grid grid-cols-2 gap-4">
      {[1, 2].map((i) => (
        <div key={i} className="p-4 bg-stone-50 rounded-lg">
          <div className="h-8 bg-stone-200 rounded w-16 mb-2" />
          <div className="h-3 bg-stone-200 rounded w-24" />
        </div>
      ))}
    </div>
    <div className="space-y-3">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-center gap-4">
          <div className="w-10 h-10 bg-stone-200 rounded" />
          <div className="flex-1">
            <div className="h-4 bg-stone-200 rounded w-24 mb-2" />
            <div className="h-2 bg-stone-200 rounded w-full" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

/**
 * Feature Usage Card Component
 */
export const FeatureUsageCard: React.FC<FeatureUsageCardProps> = ({
  data,
  isLoading = false,
  scope,
  className,
}) => {
  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-900 mb-4">Feature Usage</h3>
        <FeatureUsageSkeleton />
      </Card>
    );
  }

  return (
    <Card className={clsx('p-6', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h3 className="text-lg font-semibold text-stone-900">Feature Usage</h3>
        {scope && <ScopeBadge scope={scope} />}
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="p-4 bg-primary-50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 className="w-5 h-5 text-primary-600" />
            <span className="text-2xl font-bold text-primary-700">{data.totalActiveFeatures}</span>
          </div>
          <p className="text-sm text-primary-600">Active Features</p>
        </div>
        <div className="p-4 bg-green-50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-5 h-5 text-green-600" />
            <span className="text-2xl font-bold text-green-700">{data.overallAdoptionRate}%</span>
          </div>
          <p className="text-sm text-green-600">Overall Adoption</p>
        </div>
      </div>

      {/* Feature list */}
      <div className="space-y-4">
        {data.features.map((feature) => (
          <div key={feature.feature} className="flex items-center gap-4">
            {/* Icon */}
            <div className={clsx(
              'w-10 h-10 rounded-lg flex items-center justify-center',
              feature.adoptionRate >= 80 ? 'bg-green-100 text-green-600' :
              feature.adoptionRate >= 50 ? 'bg-amber-100 text-amber-600' :
              'bg-red-100 text-red-600'
            )}>
              {getFeatureIcon(feature.icon)}
            </div>

            {/* Details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-stone-900">{feature.feature}</span>
                <div className="flex items-center gap-2">
                  <span className={clsx('text-sm font-medium', getAdoptionColor(feature.adoptionRate))}>
                    {feature.adoptionRate}%
                  </span>
                  {feature.trend !== 0 && (
                    <Badge
                      variant={feature.trend > 0 ? 'success' : 'danger'}
                      size="sm"
                    >
                      {feature.trend > 0 ? '+' : ''}{feature.trend}%
                    </Badge>
                  )}
                </div>
              </div>
              {/* Progress bar */}
              <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                <div
                  className={clsx('h-full rounded-full transition-all', getAdoptionBarColor(feature.adoptionRate))}
                  style={{ width: `${feature.adoptionRate}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-stone-500">{feature.usageCount.toLocaleString()} uses</span>
                <span className="text-xs text-stone-500">{feature.userCount} users</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty state */}
      {data.features.length === 0 && (
        <div className="text-center py-8 text-stone-500">
          <BarChart3 className="w-12 h-12 mx-auto mb-3 text-stone-300" />
          <p>No feature usage data available</p>
        </div>
      )}
    </Card>
  );
};

export default FeatureUsageCard;
